import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import Reveal from "@/components/places/Reveal";
import AnimatedBlob from "@/components/places/AnimatedBlob";
import SEO from "@/components/places/SEO";
import ClassicBadge from "@/components/places/ClassicBadge";
import { ImplementationWrapper } from "@/components/places/InImplementationBadge";
import { pickLangObj, TRANSLATABLE_FIELDS } from "@/lib/pickLang";
import { Search, X, Grid2x2, Grid3x3, LayoutGrid, LayoutList } from "lucide-react";

const PER_ROW_OPTIONS = [2, 3, 4, 5, 6];
const PER_PAGE_OPTIONS = [6, 12, 24, 48, 96, 192];
const CASES_STATE_KEY = "places:cases:state";

const loadState = () => {
  try {
    const raw = sessionStorage.getItem(CASES_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

export default function Cases() {
  const { cases: RAW_CASES, case_categories: LEGACY_CATS, case_projects: PROJECTS = [], verticals: VERTICALS = [], padroes: PADROES = [], countries: COUNTRIES = [], home_sections = {} } = useContent();
  const { lang, t } = useLang();
  // Cases marked `is_hidden` in CMS are never shown publicly (soft-hide).
  const CASES = React.useMemo(
    () => (RAW_CASES || [])
      .filter((c) => !c.is_hidden)
      .map((c) => pickLangObj(c, lang, TRANSLATABLE_FIELDS.case)),
    [RAW_CASES, lang]
  );
  const defaultPerRow = Number(home_sections.cases_per_row) || 4;
  const defaultPerPage = Number(home_sections.cases_per_page) || 12;
  const saved = loadState();
  const [perRow, setPerRow] = useState(saved.perRow || defaultPerRow);
  const [perPage, setPerPage] = useState(saved.perPage || defaultPerPage);
  const [touched, setTouched] = useState(!!saved.touched);
  const bumpTouched = () => { if (!touched) setTouched(true); };
  const gridCls = perRow >= 6 ? "grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
    : perRow === 5 ? "grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
    : perRow === 4 ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    : perRow === 3 ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
    : "grid-cols-1 md:grid-cols-2";
  const P = t.pages || {};
  const [project, setProject] = useState(saved.project || "");
  const [vertical, setVertical] = useState(saved.vertical || "");
  const [padrao, setPadrao] = useState(saved.padrao || "");
  const [country, setCountry] = useState(saved.country || "");
  const [state_, setState] = useState(saved.state_ || "");
  const [city, setCity] = useState(saved.city || "");
  const [developer, setDeveloper] = useState(saved.developer || "");
  const [year, setYear] = useState(saved.year || "");
  const [kw, setKw] = useState(saved.kw || "");
  const [visible, setVisible] = useState(saved.visible || perPage);
  useEffect(() => { setVisible(perPage); }, [perPage]);

  // Persist state so returning from CaseDetail restores view
  useEffect(() => {
    try {
      sessionStorage.setItem(CASES_STATE_KEY, JSON.stringify({
        perRow, perPage, project, vertical, padrao, country, state_, city, developer, year, kw, visible, touched,
      }));
    } catch { /* ignore */ }
  }, [perRow, perPage, project, vertical, padrao, country, state_, city, developer, year, kw, visible, touched]);

  // Restore scroll position after returning from a case detail
  useEffect(() => {
    const y = Number(sessionStorage.getItem("places:cases:scrollY") || 0);
    if (y > 0) {
      requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" }));
      sessionStorage.removeItem("places:cases:scrollY");
    }
  }, []);

  const projectOpts = (PROJECTS && PROJECTS.length ? PROJECTS : (LEGACY_CATS || []));
  const developers = [...new Set(CASES.map((c) => c.developer).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const years = [...new Set(CASES.map((c) => c.year))].sort((a, b) => b - a);
  const cities = [...new Set(CASES.map((c) => c.city).filter(Boolean))].sort();
  const states = [...new Set(CASES.map((c) => c.state).filter(Boolean))].sort();
  const countries = COUNTRIES.length ? COUNTRIES : [...new Set(CASES.map((c) => c.country).filter(Boolean))];

  const filtered = useMemo(() => CASES.filter((c) => {
    if (project && !(c.categories || []).includes(project)) return false;
    if (vertical && !(c.verticals || []).includes(vertical)) return false;
    if (padrao && c.padrao !== padrao) return false;
    if (country && (c.country || "Brasil") !== country) return false;
    if (state_ && c.state !== state_) return false;
    if (city && c.city !== city) return false;
    if (developer && c.developer !== developer) return false;
    if (year && String(c.year) !== year) return false;
    if (kw && !`${c.title} ${c.developer} ${c.city} ${c.concept}`.toLowerCase().includes(kw.toLowerCase())) return false;
    return true;
  }), [CASES, project, vertical, padrao, country, state_, city, developer, year, kw]);

  const clear = () => { setProject(""); setVertical(""); setPadrao(""); setCountry(""); setState(""); setCity(""); setDeveloper(""); setYear(""); setKw(""); setTouched(false); try { sessionStorage.removeItem(CASES_STATE_KEY); } catch { /* ignore */ } };
  const saveScrollAndGo = () => { try { sessionStorage.setItem("places:cases:scrollY", String(window.scrollY || 0)); } catch { /* ignore */ } };
  const first = filtered[0];
  // When user has interacted with any layout control (per-row/per-page), the
  // "big featured" collapses so cases 0..N all live inside the grid.
  const gridItems = touched ? filtered.slice(0, visible) : filtered.slice(1, visible);
  const showFeatured = !touched && !!first;

  return (
    <>
      <SEO
        title={P.casesTitle || "Cases"}
        description="Portfólio de cases da Places: lançamentos, alto padrão e retrofit. Resultados, direção criativa e narrativa em cada projeto imobiliário."
        focusKeyword="cases imobiliários"
        keywords={["portfólio imobiliário", "lançamentos", "cases de marketing imobiliário", "Places"]}
        breadcrumbs={[
          { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
          { name: "Cases", url: typeof window !== "undefined" ? window.location.href : "/cases" },
        ]}
      />
      <section className="theme-light pt-36 pb-12 md:pt-44 grain relative overflow-hidden">
        <AnimatedBlob className="bg-accent-green top-10 left-0 w-[400px] h-[400px] opacity-30" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
          <Reveal><div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{P.casesEyebrow || "Cases"}</div><h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95]">{P.casesTitle || "Conheça nosso legado."}</h1></Reveal>
        </div>
      </section>

      <section className="theme-light pb-24 pt-8 md:pt-0">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          {/* Filters — mobile grid: Projeto / Tipo de Produto / (Padrão+Ano) / (Estado+Cidade) / Empresa / Palavra-chave */}
          <div className="border border-black/10 p-4 md:p-5 mb-10 grid grid-cols-2 md:flex md:flex-wrap md:items-center gap-3" data-testid="cases-filters">
            <select value={project} onChange={(e) => setProject(e.target.value)} data-testid="filter-project" className="col-span-2 md:col-auto text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Projeto</option>{projectOpts.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={vertical} onChange={(e) => setVertical(e.target.value)} data-testid="filter-vertical" className="col-span-2 md:col-auto text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Tipo de produto</option>{VERTICALS.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={padrao} onChange={(e) => setPadrao(e.target.value)} data-testid="filter-padrao" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Padrão</option>{PADROES.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="filter-year" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Ano</option>{years.map((y) => <option key={y}>{y}</option>)}</select>
            <select value={state_} onChange={(e) => setState(e.target.value)} data-testid="filter-state" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Estado</option>{states.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Cidade</option>{cities.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="filter-country" className="hidden md:block text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors"><option value="">País</option>{countries.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={developer} onChange={(e) => setDeveloper(e.target.value)} data-testid="filter-developer" className="col-span-2 md:col-auto text-sm border border-black/15 px-3 py-2 rounded-md bg-white hover:bg-black/5 transition-colors w-full md:w-auto"><option value="">Empresa</option>{developers.map((d) => <option key={d}>{d}</option>)}</select>
            <div className="col-span-2 md:col-auto flex items-center gap-2 border border-black/15 rounded-md px-3 py-2 w-full md:flex-1 md:min-w-[200px]"><Search className="w-4 h-4 text-neutral-500" /><input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Palavra-chave..." className="bg-transparent outline-none text-sm flex-1" data-testid="filter-kw" /></div>
            <div className="col-span-2 md:col-auto flex items-center gap-3 md:ml-auto justify-between">
              <button onClick={clear} data-testid="filter-clear" className="text-xs flex items-center gap-1 px-3 py-2 hover:bg-black/5 rounded-md transition-colors"><X className="w-3 h-3" /> Limpar</button>
              <span className="text-xs text-neutral-500">{filtered.length} cases</span>
            </div>
          </div>

          {/* Layout controls */}
          <div className="flex flex-wrap items-center gap-3 mb-6 pb-4 border-b border-black/5" data-testid="cases-layout-controls">
            <div className="hidden md:flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1 flex items-center gap-1"><LayoutGrid className="w-3 h-3" /> Por linha:</span>
              {PER_ROW_OPTIONS.map((n) => (
                <button key={n} onClick={() => { setPerRow(n); bumpTouched(); }} data-testid={`cases-perrow-${n}`}
                  className={`w-8 h-8 flex items-center justify-center text-xs font-semibold rounded border transition ${perRow === n ? "bg-black text-white border-black" : "border-black/15 hover:bg-black/5"}`}>
                  {n}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 flex-nowrap whitespace-nowrap">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1 flex items-center gap-1 whitespace-nowrap"><LayoutList className="w-3 h-3" /> Na página:</span>
              {PER_PAGE_OPTIONS.map((n) => (
                <button key={n} onClick={() => { setPerPage(n); bumpTouched(); }} data-testid={`cases-perpage-${n}`}
                  className={`px-2 py-1 text-[11px] md:text-xs font-semibold rounded border transition ${perPage === n ? "bg-black text-white border-black" : "border-black/15 hover:bg-black/5"}`}>
                  {n}
                </button>
              ))}
            </div>
            {touched && (
              <button onClick={() => { setTouched(false); setPerRow(defaultPerRow); setPerPage(defaultPerPage); }} data-testid="cases-reset-layout" className="ml-auto text-[10px] uppercase tracking-widest text-neutral-500 hover:text-black transition-colors px-3 py-1 rounded border border-transparent hover:border-black/15">
                Restaurar destaque
              </button>
            )}
          </div>

          {showFeatured && (
            <Reveal>
              <ImplementationWrapper active={!!first.is_in_implementation} badgeSize="lg">
                <Link to={`/cases/${first.slug}`} onClick={saveScrollAndGo} className="group block relative aspect-[4/3] md:aspect-[16/9] overflow-hidden mb-6" data-testid={`case-feat-${first.slug}`}>
                  <ClassicBadge show={!!first.is_classic} size="lg" />
                  <img src={first.cover} alt={first.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 md:p-10 text-white max-w-3xl"><div className="text-xs uppercase tracking-widest mb-2 text-accent-yellow">{(first.categories || []).join(" · ")}</div><div className="font-display text-3xl md:text-5xl">{first.title}</div><div className="text-sm opacity-80 mt-1">{first.developer} · {first.city}/{first.state} · {first.year}</div></div>
                </Link>
              </ImplementationWrapper>
            </Reveal>
          )}

          <div className={`grid gap-6 ${gridCls}`} data-testid="cases-grid">
            {gridItems.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.05}>
                <ImplementationWrapper active={!!c.is_in_implementation} badgeSize="sm">
                  <Link to={`/cases/${c.slug}`} onClick={saveScrollAndGo} className="group block" data-testid={`case-${c.slug}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-neutral-200 relative">
                      <ClassicBadge show={!!c.is_classic} size="sm" />
                      <img src={c.cover} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </div>
                    <div className="pt-4 flex flex-wrap gap-2"><span className="text-xs uppercase tracking-widest text-neutral-500">{c.developer}</span><span className="text-xs text-neutral-400">· {c.year} · {c.city}/{c.state}</span></div>
                    <div className="font-display text-2xl mt-1">{c.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{(c.categories || []).join(" · ")}</div>
                  </Link>
                </ImplementationWrapper>
              </Reveal>
            ))}
          </div>

          {visible < filtered.length && (<div className="text-center mt-12"><button onClick={() => setVisible(visible + 6)} data-testid="load-more" className="btn-sharp bg-black text-white rounded-full hover:bg-accent-yellow hover:text-black">Carregar mais</button></div>)}
        </div>
      </section>
    </>
  );
}
